/**
 * 결제 내역 콘텐츠 — 크레딧 탭 / 별도 페이지에서 공용 사용.
 *
 *  표시:
 *   - 월별 그룹핑 (최신 월 먼저)
 *   - 각 결제 row: 패키지명·결제액·받은 크레딧·상태·결제수단·일시·영수증 링크
 *   - 카드 결제는 카드 매출전표가 적격증빙 — 세금계산서 발급 X (안내 문구)
 */
import { useEffect, useState } from "react";
import {
  walletChargeApi,
  type PaymentHistoryItem,
} from "~/services/business/walletCharge";

const PAGE_SIZE = 20;

export const PaymentHistoryContent = () => {
  const [items, setItems] = useState<PaymentHistoryItem[]>([]);
  // 가상계좌 입금 대기 — 결제 완료 이력이 아니라서 별도 목록으로 온다 (첫 페이지에만)
  const [waiting, setWaiting] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [cancelTarget, setCancelTarget] = useState<PaymentHistoryItem | null>(null);

  const load = async (p: number) => {
    setLoading(true);
    try {
      const resp = await walletChargeApi.listOrders({ page: p, size: PAGE_SIZE });
      setItems(resp.content ?? []);
      setWaiting(resp.waitingDeposits ?? []);
      setTotalPages(resp.totalPages ?? 1);
      setTotalElements(resp.totalElements ?? 0);
    } catch (e) {
      console.error("[PAY-HISTORY] 조회 실패", e);
      setItems([]);
      setWaiting([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page);
  }, [page]);

  const handleCancelRequest = (it: PaymentHistoryItem) => setCancelTarget(it);
  const handleCancelDone = () => {
    setCancelTarget(null);
    load(page);
  };

  const grouped = groupByMonth(items);

  return (
    <div className="px-3 pt-3 pb-2">
      {/* 안내 — 적격증빙 문구. 필요 시 주석 해제.
      <div className="mb-3 rounded-lg border border-[#FFE3B3] bg-[#FFF7E6] p-3 text-[11px] leading-relaxed text-[#7A5C00]">
        💡 카드 결제 영수증은 <strong>적격증빙</strong>입니다. 회계 처리 시 그대로 사용 가능 — 세금계산서 별도 발급은 불필요 (이중공제 금지).
      </div>
      */}

      {loading && (
        <div className="py-12 text-center text-sm text-gray-400">로딩 중…</div>
      )}

      {/* 가상계좌 입금 대기 — 아직 충전되지 않은 진행 중 결제. 완료 내역보다 위에 둔다. */}
      {!loading && waiting.length > 0 && (
        <section className="mb-4">
          <h2 className="px-1 pb-1 text-[12px] font-bold text-[#B35F00]">입금 대기 중</h2>
          <div className="space-y-2">
            {waiting.map((it) => (
              <WaitingDepositRow key={it.orderId} item={it} />
            ))}
          </div>
        </section>
      )}

      {!loading && items.length === 0 && waiting.length === 0 && (
        <div className="py-16 text-center text-sm text-gray-500">
          아직 결제 내역이 없어요.
        </div>
      )}

      {!loading && grouped.map(({ month, list }) => (
        <section key={month} className="mb-4">
          <h2 className="px-1 pb-1 text-[12px] font-bold text-[#666]">{month}</h2>
          <div className="space-y-2">
            {list.map((it) => (
              <PaymentRow
                key={it.purchaseCreditSeq}
                item={it}
                onCancelRequest={handleCancelRequest}
              />
            ))}
          </div>
        </section>
      ))}

      {/* 페이징 */}
      {!loading && totalPages > 1 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalElements={totalElements}
          onChange={setPage}
        />
      )}

      {/* 결제 취소 확인 모달 */}
      {cancelTarget && (
        <CancelConfirmModal
          item={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onDone={handleCancelDone}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
 *  결제 1행 카드
 * ───────────────────────────────────────────────────────────────── */
function PaymentRow({
  item, onCancelRequest,
}: {
  item: PaymentHistoryItem;
  onCancelRequest: (it: PaymentHistoryItem) => void;
}) {
  const statusMeta = STATUS_META[item.paymentStatus] ?? STATUS_META.UNKNOWN;

  // 가상계좌는 PG 취소 시 환불받을 계좌 정보가 필요해 앱에서 셀프 취소를 지원하지 않는다.
  // 취소 버튼 대신 안내 문구를 노출한다.
  const isVirtualAccount = (item.paymentMethod ?? "").includes("가상계좌");
  const showVirtualAccountNotice =
    isVirtualAccount && item.paymentStatus === "COMPLETED";

  const dt = new Date(item.dtPaid);
  const dtStr = isNaN(dt.getTime())
    ? item.dtPaid
    : `${dt.getMonth() + 1}/${dt.getDate()} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;

  return (
    <div className="rounded-xl border border-[#E9E9E9] bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-base">💳</span>
            <span className="text-[14px] font-bold text-[#111] truncate">{item.orderName}</span>
          </div>
          <div className="mt-1 text-[11px] text-[#666]">
            {dtStr}
            {item.paymentMethod && <span className="ml-1 text-[#999]">· {item.paymentMethod}</span>}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[15px] font-extrabold text-[#111] tabular-nums">
            ₩{item.payAmount.toLocaleString()}
          </div>
          <div className="text-[11px] text-[#22A55B] font-semibold">
            +{item.creditAmount.toLocaleString()} 크레딧
          </div>
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2 border-t border-[#F0F0F0] pt-2">
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${statusMeta.cls}`}>
          {statusMeta.label}
        </span>
        <div className="flex gap-1">
          {item.pgReceiptUrl && (
            <a
              href={item.pgReceiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-[#E9E9E9] px-2 py-1 text-[11px] text-[#666] hover:border-[#FF9934] hover:text-[#FF9934]"
            >
              📄 영수증
            </a>
          )}
          {item.canCancel && (
            <button
              type="button"
              onClick={() => onCancelRequest(item)}
              className="rounded-md border border-red-300 bg-red-50 px-2 py-1 text-[11px] font-medium text-red-600 hover:bg-red-100"
            >
              ↶ 결제 취소
            </button>
          )}
        </div>
      </div>

      {showVirtualAccountNotice && (
        <div className="mt-2 rounded-md bg-[#F7F7F7] px-2 py-1.5 text-[11px] leading-relaxed text-[#666]">
          가상계좌 결제에 대한 취소는 관리자에게 직접 연락 바랍니다.
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  가상계좌 입금 대기 1행
 *   — 아직 충전 전이므로 "받을 크레딧" 으로 표기하고 취소 버튼은 두지 않는다.
 *     (입금 전 계좌는 취소할 대상이 없고, 기한이 지나면 자동 만료된다)
 * ───────────────────────────────────────────────────────────────── */
function WaitingDepositRow({ item }: { item: PaymentHistoryItem }) {
  const due = item.dtDepositDue ? new Date(item.dtDepositDue) : null;
  const dueStr = due && !isNaN(due.getTime())
    ? `${due.getMonth() + 1}/${due.getDate()} ${String(due.getHours()).padStart(2, "0")}:${String(due.getMinutes()).padStart(2, "0")} 까지`
    : null;

  const copy = async () => {
    if (!item.virtualAccountNumber) return;
    try {
      await navigator.clipboard.writeText(item.virtualAccountNumber);
    } catch {
      // 앱뷰에서 clipboard 가 막힌 경우 — 길게 눌러 직접 복사
    }
  };

  return (
    <div className="rounded-xl border border-[#FFE3B3] bg-[#FFF7EC] p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-base">🏦</span>
            <span className="truncate text-[14px] font-bold text-[#111]">{item.orderName}</span>
          </div>
          {dueStr && (
            <div className="mt-1 text-[11px] text-[#B35F00]">입금 기한 {dueStr}</div>
          )}
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[15px] font-extrabold tabular-nums text-[#111]">
            ₩{item.payAmount.toLocaleString()}
          </div>
          <div className="text-[11px] font-semibold text-[#8A5A1E]">
            입금 시 +{item.creditAmount.toLocaleString()} 크레딧
          </div>
        </div>
      </div>

      <div className="mt-2 border-t border-[#F3E1C6] pt-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] text-[#8A5A1E]">
              {item.virtualAccountBankName ?? "-"}
              {item.virtualAccountHolder && (
                <span className="ml-1 text-[#B08A5A]">· 예금주 {item.virtualAccountHolder}</span>
              )}
            </div>
            <div className="break-all font-mono text-[14px] font-bold text-[#111]">
              {item.virtualAccountNumber ?? "-"}
            </div>
          </div>
          {item.virtualAccountNumber && (
            <button
              type="button"
              onClick={copy}
              className="shrink-0 rounded-md border border-[#FF9934] px-2 py-1 text-[11px] font-semibold text-[#FF9934]"
            >
              복사
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  결제 취소 확인 모달 — 7일 이내 미사용 100% 환불
 * ───────────────────────────────────────────────────────────────── */
const CANCEL_REASONS = [
  "충전을 잘못했어요",
  "다른 결제 수단으로 다시 결제할게요",
  "사용 계획이 바뀌었어요",
  "단순 변심",
  "기타",
] as const;

type CancelReason = (typeof CANCEL_REASONS)[number];

function CancelConfirmModal({
  item, onClose, onDone,
}: {
  item: PaymentHistoryItem;
  onClose: () => void;
  onDone: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [reason, setReason] = useState<CancelReason | "">("");
  const [customReason, setCustomReason] = useState("");

  // 기타 선택 시 직접 입력값 사용, 그 외엔 라디오 선택 라벨 사용
  const finalReason = reason === "기타" ? customReason.trim() : reason;
  const canConfirm = reason !== "" && !(reason === "기타" && customReason.trim().length === 0);

  const handleConfirm = async () => {
    if (saving || !canConfirm) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      await walletChargeApi.cancelOrder(item.orderId, finalReason);
      onDone();
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "결제 취소 실패");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 px-3"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md max-h-[calc(100vh-100px)] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-2 text-lg font-bold text-[#111]">결제 취소하기</h3>
        <p className="mb-3 text-[13px] text-[#666] leading-relaxed">
          아래 결제를 취소하시겠어요?<br />
          <strong className="text-red-600">결제 금액 전액이 카드사로 환불</strong>되고
          충전된 크레딧은 회수됩니다.
        </p>

        <div className="mb-3 rounded-lg bg-gray-50 p-3 text-[13px]">
          <div className="flex justify-between">
            <span className="text-[#666]">상품</span>
            <span className="font-medium text-[#111]">{item.orderName}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-[#666]">환불 금액</span>
            <span className="font-bold text-red-600">₩{item.payAmount.toLocaleString()}</span>
          </div>
          <div className="mt-1 flex justify-between">
            <span className="text-[#666]">회수 크레딧</span>
            <span className="font-bold text-[#111]">−{item.creditAmount.toLocaleString()}</span>
          </div>
        </div>

        {/* 취소 사유 선택 */}
        <div className="mb-3">
          <label className="text-[12px] font-semibold text-[#111]">
            취소 사유 <span className="text-red-500">*</span>
          </label>
          <div className="mt-1.5 space-y-1.5">
            {CANCEL_REASONS.map((r) => {
              const selected = reason === r;
              return (
                <label
                  key={r}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                    selected
                      ? "border-[#FF9934] bg-[#FFF7E6]"
                      : "border-[#E9E9E9] bg-white hover:border-[#FFB870]"
                  }`}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    checked={selected}
                    onChange={() => setReason(r)}
                    className="accent-[#FF9934]"
                  />
                  <span className="text-[13px] text-[#111]">{r}</span>
                </label>
              );
            })}
          </div>
          {reason === "기타" && (
            <input
              type="text"
              autoFocus
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              placeholder="취소 사유를 입력해주세요"
              maxLength={200}
              className="mt-1.5 w-full rounded-md border border-[#E9E9E9] px-3 py-2 text-sm focus:border-[#FF9934] focus:outline-none focus:ring-1 focus:ring-[#FF9934]"
            />
          )}
        </div>

        <div className="mb-4 rounded-lg border border-[#FFE3B3] bg-[#FFF7E6] p-2.5 text-[11px] leading-relaxed text-[#7A5C00]">
          💡 7일 이내 + 충전 후 미사용 결제는 위약금 없이 100% 환불 가능합니다.
          환불 처리는 카드사에 따라 영업일 기준 3~5일 소요될 수 있어요.
        </div>

        {errorMsg && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-2 text-[12px] text-red-700">
            ⚠ {errorMsg}
          </div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex-1 rounded-md border border-[#D9D9D9] bg-white py-3 text-sm font-medium text-[#666] hover:bg-gray-50"
          >
            닫기
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={saving || !canConfirm}
            style={{
              backgroundColor: saving || !canConfirm ? "#FCA5A5" : "#DC2626",
              color: "#fff",
              boxShadow: "0 2px 6px rgba(220, 38, 38, 0.25)",
            }}
            className="flex-[1.4] rounded-md py-3 text-sm font-bold disabled:cursor-not-allowed"
          >
            {saving ? "처리 중..." : "결제 취소 확정"}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  COMPLETED:         { label: '✅ 완료',      cls: 'bg-emerald-50 text-emerald-700' },
  CANCELLED:        { label: '❌ 취소됨',    cls: 'bg-red-50 text-red-700' },
  REFUNDED:         { label: '↶ 환불 완료',  cls: 'bg-gray-100 text-gray-600' },
  PARTIAL_REFUNDED: { label: '⚠ 부분 환불',  cls: 'bg-amber-50 text-amber-700' },
  WAITING_DEPOSIT:  { label: '🏦 입금 대기',  cls: 'bg-[#FFF7EC] text-[#B35F00]' },
  UNKNOWN:          { label: '?',           cls: 'bg-gray-100 text-gray-600' },
};

/* ─────────────────────────────────────────────────────────────────
 *  페이징
 * ───────────────────────────────────────────────────────────────── */
function Pagination({
  page, totalPages, totalElements, onChange,
}: {
  page: number;
  totalPages: number;
  totalElements: number;
  onChange: (p: number) => void;
}) {
  const display = page + 1;
  return (
    <div className="mt-3 flex items-center justify-center gap-2 text-[12px] text-[#666]">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, page - 1))}
        disabled={page <= 0}
        className="rounded-md border border-[#E9E9E9] px-3 py-1 disabled:opacity-40"
      >
        이전
      </button>
      <span>{display} / {totalPages} ({totalElements}건)</span>
      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages - 1, page + 1))}
        disabled={page >= totalPages - 1}
        className="rounded-md border border-[#E9E9E9] px-3 py-1 disabled:opacity-40"
      >
        다음
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  월별 그룹핑
 * ───────────────────────────────────────────────────────────────── */
function groupByMonth(items: PaymentHistoryItem[]): Array<{ month: string; list: PaymentHistoryItem[] }> {
  const buckets: Record<string, PaymentHistoryItem[]> = {};
  const order: string[] = [];
  for (const it of items) {
    const dt = new Date(it.dtPaid);
    const key = isNaN(dt.getTime())
      ? "기타"
      : `${dt.getFullYear()}년 ${dt.getMonth() + 1}월`;
    if (!buckets[key]) {
      buckets[key] = [];
      order.push(key);
    }
    buckets[key].push(it);
  }
  return order.map((m) => ({ month: m, list: buckets[m] }));
}
